import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IconChevronDown, IconSparkles } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Group, Stack, Text, UnstyledButton } from '@mantine/core';
import * as classes from './styles/index.css';

interface ThinkingFeedProps {
  steps: string[];
  isStreaming: boolean;
}

function AnimatedProgressBar() {
  return (
    <div className={classes.progressBar}>
      <motion.div
        style={{
          height: '100%',
          width: '40%',
          borderRadius: 1,
          background:
            'linear-gradient(90deg, var(--mantine-color-indigo-2), var(--mantine-color-indigo-5), var(--mantine-color-indigo-2))',
        }}
        animate={{ x: ['-100%', '250%'] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
      />
    </div>
  );
}

function ThinkingFeed({ steps, isStreaming }: ThinkingFeedProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (steps.length === 0 && !isStreaming) return null;

  if (isStreaming) {
    const previousSteps = steps.slice(0, -1);
    const currentStep =
      steps.length > 0
        ? steps[steps.length - 1]
        : t('AIAssistant:Chat.thinking.processing');

    return (
      <Stack gap={2} style={{ overflow: 'hidden' }}>
        {previousSteps.map((step, stepIndex) => (
          <motion.div
            key={`step-${stepIndex}-${step.slice(0, 20)}`}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15 }}
          >
            <Text size="sm" c="gray.5" lh={1.4}>
              {step}
            </Text>
          </motion.div>
        ))}
        <motion.div
          key={`active-${steps.length}`}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15 }}
        >
          <Group gap={6} wrap="nowrap">
            <motion.span
              animate={{ rotate: [0, 15, -15, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              style={{ display: 'flex' }}
            >
              <IconSparkles size={14} />
            </motion.span>
            <Text size="sm" c="gray.6" fw={500} lh={1.4}>
              {currentStep}
            </Text>
          </Group>
        </motion.div>
        <AnimatedProgressBar />
      </Stack>
    );
  }

  return (
    <div>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        <UnstyledButton
          className={classes.collapsedTrigger}
          onClick={() => setExpanded((prev) => !prev)}
          px="xs"
          py={4}
          style={{
            borderRadius: 'var(--mantine-radius-xl)',
            backgroundColor: 'var(--mantine-color-gray-1)',
            transition: 'background-color 0.15s ease',
          }}
        >
          <Group gap={6} wrap="nowrap">
            <IconSparkles size={14} />
            <Text size="sm" c="gray.6">
              {t('AIAssistant:Chat.thinking.analyzed', { count: steps.length })}
            </Text>
            <motion.span
              animate={{ rotate: expanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              style={{ display: 'flex' }}
            >
              <IconChevronDown size={12} />
            </motion.span>
          </Group>
        </UnstyledButton>
      </motion.div>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
          >
            <Stack gap={2} mt="xs" style={{ overflow: 'hidden' }}>
              {steps.map((step, stepIndex) => (
                <motion.div
                  key={`step-${stepIndex}-${step.slice(0, 20)}`}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15, delay: stepIndex * 0.03 }}
                >
                  <Text size="sm" c="gray.5" lh={1.4}>
                    {step}
                  </Text>
                </motion.div>
              ))}
            </Stack>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ThinkingFeed;
